import React, { useState } from 'react';
import { useStoryTranslation } from 'admin/queries/story/use-story-translation';
import { getLanguageOptions } from 'common/i18n/utils';
import { Modal, ModalBody, ModalFooter } from 'admin/components/common/Modal';
import { ModalManagerProvidedProps } from 'admin/components/common/ModalManager';
import { MODAL } from 'admin/constants/common';
import Text from 'admin/components/common/Text';
import { Hint } from 'admin/components/common/Form/Hint';
import { Icon } from 'admin/components/common/Icon';
import Button from 'admin/components/common/Button';
import { Select } from 'admin/components/common/Form';
import { toast } from 'admin/components/common/MessageContainer/toast';
import t from 'common/utils/translate';
import type { SupportedLanguage } from 'common/i18n/types';

type Props = ModalManagerProvidedProps<MODAL.TRANSLATE_STORY>;

const TranslationModal: React.FC<Props> = ({ open, ...props }) => {
	const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage | null>(null);
	const [isBusy, setIsBusy] = useState(false);
	const [loadingStatus, setLoadingStatus] = useState<string | undefined>();
	const languageOptions = getLanguageOptions(props.data.language);

	const { translateStory } = useStoryTranslation();

	const handleTranslate = async (_selectedLanguage: SupportedLanguage) => {
		setIsBusy(true);
		setLoadingStatus(t('translationModal.status.starting'));

		try {
			await translateStory(props.data.storyId, _selectedLanguage, setLoadingStatus);
			toast.success(t('translationModal.messages.success'));
			props.close();
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : t('translationModal.status.failed');
			toast.error(t('translationModal.messages.failed', { error: errorMessage }));
		} finally {
			setIsBusy(false);
		}
	};

	const handleClose = () => {
		if (!isBusy) {
			setSelectedLanguage(null);
			setLoadingStatus(undefined);
			props.close();
		}
	};

	const handleLanguageChange = (value: SupportedLanguage) => {
		setSelectedLanguage(value);
	};

	if (!props.data.storyId) {
		return null;
	}

	return (
		<Modal
			title={
				<Text
					size="subtitle"
					weight="semibold"
					text={t('translationModal.title', { name: props.data.storyName })}
				/>
			}
			open={open}
			destroyOnClose
			width={564}
			maskClosable={!isBusy}
			closable={!isBusy}
		>
			<ModalBody>
				<Select
					label={{ children: t('translationModal.select.label') }}
					options={languageOptions}
					placeholder={t('translationModal.select.placeholder')}
					value={selectedLanguage}
					eventListeners={{ onChange: handleLanguageChange }}
					disabled={isBusy}
					isSearchable
				/>
				<Hint
					style={{ marginTop: 8 }}
					text={
						<>
							<Icon type="info" color="var(--ra-color-black-20)" width={14} />
							&nbsp;{t('translationModal.hint')}
						</>
					}
				/>
			</ModalBody>
			<ModalFooter>
				<Button onClick={handleClose} disabled={isBusy} view="secondary">
					{t('translationModal.btn.cancel')}
				</Button>
				<Button
					loading={isBusy}
					disabled={!selectedLanguage || isBusy}
					onClick={selectedLanguage ? () => handleTranslate(selectedLanguage) : undefined}
					view="primary"
				>
					{isBusy
						? loadingStatus || t('translationModal.status.translating')
						: t('translationModal.btn.translate')}
				</Button>
			</ModalFooter>
		</Modal>
	);
};

export default TranslationModal;
